import * as yup from "yup";
import React, { useState, useEffect, useRef } from "react";
import NumberFormatCustom from "../../FormUI/PTBRcurrency";
import ptBRLocale from "date-fns/locale/pt-BR";
import { Form, Formik } from "formik";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Grid2,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { TitleStyled, WrapperStyled } from "../PartnerShareDetails/styles";
import { FormContainer, MoneyIconStyled } from "./styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useDataFormContext } from "@/context/FormDataContext";
import { register } from "@/services/lead";
import { IRegisterResponse } from "@/services/index";
import NonPartnerShareMessage from "../NonPartnerShareMessage";

import { LoadingButton } from "@mui/lab";

const mandatory = "Campo obrigatório";
const valueExceed = "Valor muito alto";
const valueBelow = "Valor muito baixo";

/**
 *
 * @returns NonPartnerShareDetails components
 */
export default function NonPartnerShareDetails(): React.JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    dataForm,
    currentAdm,
    alreadyShowed,
    metaDataTimes,
    blockNonPartnerFlow,
    setMetaDataTimes,
    setCurrentFormStep,
    setCurrentFormVariation,
    setDataForm,
  } = useDataFormContext();
  const [showMessage, setShowMessage] = useState(
    blockNonPartnerFlow
      ? true
      : !currentAdm.workable && !alreadyShowed
        ? true
        : false
  );

  const INITIAL_FORM_STATE = {
    goodValue: "",
    goodType: "1",
    remainingPayments: 12,
    amountOwed: "",
    awarded: "n",
    awardedDate: new Date(),
    situation: "n",
  };

  const FORM_VALIDATION = yup.object().shape({
    goodValue: yup
      .number()
      .test("moreThan", valueBelow, function (value) {
        return value && 10 ** 3 > value / 100 ? false : true;
      })
      .test("lessThan", valueExceed, function (value) {
        return value && 3 * 10 ** 6 < value / 100 ? false : true;
      })
      .required(mandatory),
    goodType: yup.string().required(mandatory),
    remainingPayments: yup
      .number()
      .moreThan(1, valueBelow)
      .lessThan(12 * 30, valueExceed)
      .required(mandatory),
    amountOwed: yup
      .number()
      .test("moreThan", valueBelow, function (value) {
        return value && 10 > value / 100 ? false : true;
      })
      .test("lessThan", valueExceed, function (value) {
        if (value) {
          // eslint-disable-next-line no-invalid-this
          return value < 1.5 * this.parent.goodValue;
        }
        return true;
      })
      .required(mandatory),
    awarded: yup.string().required(mandatory),
    situation: yup.string().required(mandatory),
  });

  /**
   * update Metadata
   * @return {void}
   */
  function getAdvancedToStepThree(): void {
    setMetaDataTimes({
      ...metaDataTimes,
      advancedToStepThree: String(new Date().valueOf()),
    });
  }

  /**
   * @param values
   */
  async function submitForm(values: any) {
    setIsLoading(true);
    const formPayload = {
      ...dataForm,
      ...{
        formStep: 2,
        value: values.goodValue / 100,
        goodType: Number(values.goodType),
        remainingPayments: values.remainingPayments,
        ownedAmount: values.amountOwed / 100,
        isAwarded: values.awarded,
        isCanceled: values.situation,
        awardedDate: values.awardedDate,
        metadata: { ...dataForm.metadata, formData: { ...metaDataTimes } },
      },
    };

    setDataForm(formPayload);

    try {
      const registerResponse = await register(formPayload);
      const registerResponseData =
        registerResponse.data as unknown as IRegisterResponse;

      if (registerResponseData.id) {
        setDataForm((current) => ({
          ...current,
          traceId: registerResponseData.traceId ?? current.traceId,
        }));
        setCurrentFormStep(2);
        setIsLoading(false);
        getAdvancedToStepThree();
      } else {
        throw new Error("erro ao enviar dados");
      }
    } catch (error) {
      console.error(error);
    }
  }

  const inputRef = useRef(null) as any;
  useEffect(() => {
    if (inputRef.current) {
      const height = inputRef?.current?.offsetHeight;
      window.parent.postMessage(
        ["setHeight", `${height + (height * 54) / 100}`],
        "*"
      );
    }
  }, []);

  return (
    <FormContainer>
      <Formik
        initialValues={INITIAL_FORM_STATE}
        validationSchema={FORM_VALIDATION}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => {
          return (
            <Form ref={inputRef} id="formNonPartner">
              <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12 }}>
                  <TitleStyled>Dados da sua cota de consórcio</TitleStyled>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="tel"
                    id="goodValue"
                    name="goodValue"
                    label="Valor do Bem/Crédito"
                    error={touched.goodValue && Boolean(errors.goodValue)}
                    helperText={touched.goodValue && errors.goodValue}
                    value={values.goodValue}
                    onChange={handleChange}
                    onKeyUp={handleBlur}
                    placeholder="R$ 00,00"
                    slotProps={{
                      input: {
                        inputMode: "numeric",
                        inputComponent: NumberFormatCustom as any,
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIconStyled />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormControl>
                    <FormLabel id="goodType">Tipo do Bem</FormLabel>
                    <RadioGroup
                      row
                      name="goodType"
                      id="goodType"
                      aria-labelledby="goodType"
                      value={values.goodType}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label="Veículos"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio />}
                        label="Imóveis"
                      />
                      <FormControlLabel
                        value="3"
                        control={<Radio />}
                        label="Serviços"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    id="remainingPayments"
                    name="remainingPayments"
                    label="Parcelas restantes"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.remainingPayments}
                    slotProps={{
                      input: {
                        inputMode: "numeric",
                      },
                    }}
                    error={
                      touched.remainingPayments &&
                      Boolean(errors.remainingPayments)
                    }
                    helperText={
                      touched.remainingPayments && errors.remainingPayments
                    }
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="tel"
                    id="amountOwed"
                    name="amountOwed"
                    label="Saldo devedor (valor para quitação)"
                    error={touched.amountOwed && Boolean(errors.amountOwed)}
                    helperText={touched.amountOwed && errors.amountOwed}
                    onChange={handleChange}
                    onKeyUp={handleBlur}
                    value={values.amountOwed}
                    placeholder="R$ 00,00"
                    slotProps={{
                      input: {
                        inputMode: "numeric",
                        inputComponent: NumberFormatCustom as any,
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIconStyled />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: values.awarded === "y" ? 3 : 6 }}>
                  <FormControl>
                    <FormLabel id="awardedLabel">
                      Sua cota está <strong>contemplada</strong>?
                    </FormLabel>
                    <RadioGroup
                      row
                      name="awarded"
                      id="awarded"
                      aria-labelledby="awardedLabel"
                      value={values.awarded}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="y"
                        control={<Radio />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value="n"
                        control={<Radio />}
                        label="Não"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>

                {values.awarded === "y" && (
                  <Grid2 size={{ xs: 12, sm: 3 }}>
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={ptBRLocale as any}
                    >
                      <DesktopDatePicker
                        disableFuture={true}
                        value={values.awardedDate}
                        onChange={(value) => {
                          value &&
                            setFieldValue("awardedDate", value.getTime());
                        }}
                        slotProps={{
                          textField: {
                            name: "awardedDate",
                            id: "awardedDate",
                            label: "Data de contemplação",
                            onBlur: () => {
                              values.awardedDate &&
                                setFieldValue(
                                  "awardedDate",
                                  values.awardedDate.getTime()
                                );
                            },
                          },
                        }}
                        format="dd/MM/yyyy"
                      />
                    </LocalizationProvider>
                  </Grid2>
                )}

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormControl>
                    <FormLabel id="situation">
                      Sua cota está <strong>cancelada</strong>?
                    </FormLabel>
                    <RadioGroup
                      row
                      name="situation"
                      id="situation"
                      aria-labelledby="situation"
                      value={values.situation}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="y"
                        control={<Radio />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value="n"
                        control={<Radio />}
                        label="Não"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <WrapperStyled>
                    <Button
                      variant="goBack"
                      onClick={() => {
                        setCurrentFormStep(0);
                        setCurrentFormVariation("all");
                      }}
                    >
                      voltar
                    </Button>
                    <LoadingButton
                      loading={isLoading}
                      variant="goFoward"
                      type="submit"
                    >
                      continuar
                    </LoadingButton>
                  </WrapperStyled>
                </Grid2>
                {/* <pre>{JSON.stringify(errors, null, 4)}</pre> */}
                {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
              </Grid2>
            </Form>
          );
        }}
      </Formik>
      {showMessage && (
        <NonPartnerShareMessage
          name={dataForm.name}
          onContinueClick={() => {
            setShowMessage(false);
          }}
        />
      )}
    </FormContainer>
  );
}
